import type { Import, Me } from "@riea-water/contracts";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useOutletContext } from "react-router";
import {
  useCommitImport,
  useConnections,
  useImports,
  useQuarantine,
  useUploadImport,
} from "../api/queries.js";
import type { ShellContext } from "../shell/Shell.js";
import { useToast } from "../shell/toast.js";
import {
  connectionsFor,
  ENTITY_KINDS,
  entityKindLabel,
  number,
  reasonBars,
  statusLabel,
} from "./imports-helpers.js";

/** A form field as text; a file or a missing field is the empty string. */
function formText(form: FormData, name: string): string {
  const value = form.get(name);
  return typeof value === "string" ? value : "";
}

const CAN_COMMIT: readonly Me["role"][] = ["tenant_admin", "data_lead"];
const CAN_UPLOAD: readonly Me["role"][] = ["tenant_admin", "data_lead", "analyst"];

/** Setup · Imports (IMP-01 to IMP-05), as the prototype draws it: the table of extracts, the reason bars, Upload and Commit. */
export function Imports() {
  const { me, client, getToken } = useOutletContext<ShellContext>();
  const imports = useImports(client);
  const connections = useConnections(client);
  const commit = useCommitImport(client);
  const upload = useUploadImport(client, getToken);
  const { toast, Toast } = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);

  const items = imports.data?.items ?? [];
  const selected =
    items.find((i) => i.id === selectedId) ??
    items.find((i) => i.counts.quarantined > 0) ??
    items[0] ??
    null;
  const quarantine = useQuarantine(
    client,
    selected && selected.counts.quarantined > 0 ? selected.id : null,
  );
  useEffect(() => {
    if (selected && selectedId === null) setSelectedId(selected.id);
  }, [selected, selectedId]);

  const onCommit = (item: Import) => {
    commit.mutate(item.id, {
      onSuccess: () =>
        toast(`Committing ${item.fileName} · ImportCommitted is recorded when it finishes`),
      onError: (error) => toast(`Commit refused · ${error.message}`),
    });
  };

  const onUpload = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const file = form.get("file");
    const entityKind = formText(form, "entityKind");
    const connectionId = formText(form, "connectionId");
    if (!(file instanceof File) || file.size === 0 || !entityKind || !connectionId) return;
    upload.mutate(
      { entityKind, connectionId, file },
      {
        onSuccess: (imported) => {
          dialog.current?.close();
          setSelectedId(imported.id);
          toast(
            `${imported.fileName} received through ${connectionId} · ImportStarted recorded${imported.counts.quarantined > 0 ? ` · ${number(imported.counts.quarantined)} rows quarantined` : ""}`,
          );
        },
        onError: (error) => toast(`Upload refused · ${error.message}`),
      },
    );
  };

  return (
    <>
      <div className="head">
        <div>
          <div className="eyebrow">Setup</div>
          <h1>Imports</h1>
        </div>
        {CAN_UPLOAD.includes(me.role) ? (
          <button
            className="btn primary sm"
            type="button"
            onClick={() => dialog.current?.showModal()}
          >
            Upload extract
          </button>
        ) : null}
      </div>

      <section className="card">
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th>File</th>
                <th>Entity</th>
                <th className="num">Rows</th>
                <th className="num">Staged</th>
                <th className="num">Quarantined</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {imports.isPending ? (
                [0, 1, 2].map((i) => (
                  <tr key={i}>
                    <td colSpan={6}>
                      <div className="skeleton" />
                    </td>
                  </tr>
                ))
              ) : imports.isError ? (
                <tr>
                  <td colSpan={6} className="empty">
                    The imports could not be loaded: {imports.error.message}. Try again in a moment.
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty">
                    No extracts yet. Upload one, or drop files into a connection&apos;s directory
                    and run a sync from Connections.
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr
                    key={item.id}
                    className="click"
                    aria-selected={selected?.id === item.id}
                    onClick={() => setSelectedId(item.id)}
                  >
                    <td className="mono" style={{ fontSize: ".78rem" }}>
                      {item.fileName}
                    </td>
                    <td>{entityKindLabel(item.entityKind)}</td>
                    <td className="num">{number(item.counts.read)}</td>
                    <td className="num">{number(item.counts.staged)}</td>
                    <td className="num">
                      {item.counts.quarantined > 0 ? (
                        <span style={{ color: "var(--warn)" }}>
                          {number(item.counts.quarantined)}
                        </span>
                      ) : (
                        "0"
                      )}
                    </td>
                    <td>
                      <ImportStatus
                        item={item}
                        canCommit={CAN_COMMIT.includes(me.role)}
                        onCommit={onCommit}
                        busy={commit.isPending}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {selected && selected.counts.quarantined > 0 ? (
        <section className="card">
          <header>
            <h2>Quarantine reasons · {entityKindLabel(selected.entityKind).toLowerCase()} file</h2>
            <span className="muted mono" style={{ fontSize: ".74rem" }}>
              {selected.fileName}
            </span>
          </header>
          <div className="body">
            {reasonBars(selected.quarantineByReason).map((bar) => (
              <div className="hbar" key={bar.code}>
                <span>{bar.label}</span>
                <span className="track">
                  <i style={{ width: `${bar.width}%`, background: "var(--warn)" }} />
                </span>
                <span className="num">{number(bar.count)}</span>
              </div>
            ))}
            {quarantine.data && quarantine.data.items.length > 0 ? (
              <details style={{ marginTop: 10 }}>
                <summary className="muted" style={{ fontSize: ".8rem", cursor: "pointer" }}>
                  First {number(quarantine.data.items.length)} quarantined rows with line numbers
                </summary>
                <div className="tablewrap" style={{ marginTop: 8 }}>
                  <table>
                    <thead>
                      <tr>
                        <th className="num">Line</th>
                        <th>Reason</th>
                        <th>Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quarantine.data.items.map((row) => (
                        <tr key={row.line}>
                          <td className="num">{number(row.line)}</td>
                          <td>{reasonBars({ [row.reason]: 1 })[0]?.label}</td>
                          <td className="mono" style={{ fontSize: ".76rem" }}>
                            {row.detail}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            ) : null}
          </div>
        </section>
      ) : null}

      <dialog className="sheet" ref={dialog} aria-labelledby="upload-title">
        <form onSubmit={onUpload} method="dialog">
          <div>
            <div className="eyebrow">Setup · Imports</div>
            <h2 id="upload-title" style={{ marginTop: 4 }}>
              Upload extract
            </h2>
          </div>
          <p className="muted" style={{ margin: 0, fontSize: ".84rem" }}>
            One file per entity, in the module&apos;s template columns or with a saved mapping. The
            file arrives through the connection you choose and waits for Commit.
          </p>
          <UploadFields connections={connections.data?.items ?? []} />
          <div className="split" style={{ justifyContent: "flex-end" }}>
            <button className="btn sm ghost" type="button" onClick={() => dialog.current?.close()}>
              Cancel
            </button>
            <button className="btn sm primary" type="submit" disabled={upload.isPending}>
              {upload.isPending ? "Uploading" : "Upload"}
            </button>
          </div>
        </form>
      </dialog>
      <Toast />
    </>
  );
}

function ImportStatus(props: {
  item: Import;
  canCommit: boolean;
  busy: boolean;
  onCommit: (item: Import) => void;
}) {
  const { item } = props;
  if (item.status === "committed") return <span className="pill ok">Committed</span>;
  if (item.status === "ready_to_commit") {
    if (!props.canCommit) return <span className="pill info">Ready to commit</span>;
    return (
      <button
        className="btn sm primary"
        type="button"
        disabled={props.busy}
        onClick={(e) => {
          e.stopPropagation();
          props.onCommit(item);
        }}
      >
        Commit
      </button>
    );
  }
  if (item.status === "failed")
    return (
      <span className="pill crit" title={item.detail ?? undefined}>
        Failed
      </span>
    );
  return <span className="pill info">{statusLabel(item.status)}</span>;
}

function UploadFields(props: { connections: ReturnType<typeof connectionsFor> }) {
  const [kind, setKind] = useState(ENTITY_KINDS[0]!.value);
  const candidates = connectionsFor(kind, props.connections);
  return (
    <>
      <label>
        <span className="eyebrow">Entity</span>
        <select name="entityKind" value={kind} onChange={(e) => setKind(e.target.value)}>
          {ENTITY_KINDS.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span className="eyebrow">Through connection</span>
        <select name="connectionId" key={kind} defaultValue={candidates[0]?.id ?? ""}>
          {candidates.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        {candidates.length === 0 ? (
          <small className="faint">
            No registered connection accepts {entityKindLabel(kind).toLowerCase()} files.
          </small>
        ) : null}
      </label>
      <label>
        <span className="eyebrow">File (CSV, up to 500 MB)</span>
        <input type="file" name="file" accept=".csv,text/csv" required />
      </label>
    </>
  );
}
